import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import MessageBox, {MessageType} from "../components/MessageBox";
import {Tour} from "../../models/tour";
import TourCard from "../cards/TourCard";
import {TourMembership} from "../../models/membership";
import {getTourMembershipsByAccount} from "../../api/membershipTourApi";
import {TourMembershipDialog} from "../dialogs/TourMembershipDialog";
import TourDialog from "../dialogs/TourDialog";
import {getEmergencyContactsByAccount} from "../../api/emergencyContactApi";
import {EmergencyContact} from "../../models/emergencyContact";
import TourCreateForm from "../forms/tour/TourCreateForm";
import TourSearchDialog from "../dialogs/TourSearchDialog";
import {useNavigate, useSearchParams} from "react-router-dom";
import {QUERY_PARAMS} from "../../constants/queryParams";
import {ROUTES} from "../../constants/navigation";
import Menu from "../menu/Menu";
import {isDateInPast} from "../../utils/formatDateTime";
import {useRequiredAccount} from "../../hooks/AccountContext";
import {useDbUpdates} from "../../hooks/useDbUpdates";

export default function Tours() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [tourMemberships, setTourMemberships] = useState<TourMembership[]>([]);
    const [contacts, setContacts] = useState<EmergencyContact[]>([]);
    const [message, setMessage] = useState<MessageType>(null);
    const [disableMenu, setDisableMenu] = useState<boolean>(false);

    const [tourDialogMode, setTourDialogMode] = useState<"edit" | "create" | null>(null);
    const [membershipDialogMode, setMembershipDialogMode] = useState<"edit" | "create" | null>(null);

    const [editTour, setEditTour] = useState<Tour | null>(null);
    const [editMembership, setEditMembership] = useState<TourMembership | null>(null);

    const [showSearchDialog, setShowSearchDialog] = useState<boolean>(false);
    const [hideHistorical, setHideHistorical] = useState<boolean>(() => {
        const stored = localStorage.getItem("hideHistorical");
        return stored ? stored === "true" : true;
    });
    const [timeTrigger, setTimeTrigger] = useState(0);

    const hideHistoricalLabel = hideHistorical
        ? t("tours.buttons.showHistoricalTours")
        : t("tours.buttons.hideHistoricalTours");

    // 🔥 Globaler Account-State aus dem Context
    const {
        account
    } = useRequiredAccount();

    useEffect(() => {
        if (!tourMemberships.length) return;

        const now = Date.now();

        // Alle zukünftigen Endzeiten sammeln
        const futureEnds = tourMemberships
            .map(m => new Date(m.tour.validTo).getTime())
            .filter(end => end > now);

        if (futureEnds.length === 0) return;

        // Nächste Tour, die abläuft
        const nextEnd = Math.min(...futureEnds);

        const delay = nextEnd - now;

        const timer = setTimeout(() => {
            // Löst ein Re-Render aus → hideTour() wird neu berechnet
            setTimeTrigger(Date.now());
        }, delay);

        return () => clearTimeout(timer);
    }, [tourMemberships]);

    const title = <h2>{t("menu.tours")}</h2>;

    const loadAll = async () => {
        try {
            const memberships = await getTourMembershipsByAccount(account.id);
            setTourMemberships(memberships);

            // 🔥 Wenn ein Membership-Dialog offen ist → aktualisierte Membership suchen
            if (editMembership) {
                const updated = memberships.find(
                    m =>
                        m.tour.id === editMembership.tour.id &&
                        m.account.id === editMembership.account.id
                );

                if (updated) {
                    setEditMembership(updated);
                    setEditTour(updated.tour);
                } else {
                    // Falls Membership gelöscht wurde
                    setEditMembership(null);
                    setEditTour(null);
                    setMembershipDialogMode(null);
                }
            }

            const contactsData = await getEmergencyContactsByAccount(account.id);
            setContacts(contactsData);

            setDisableMenu(false);
            setMessage(null);
        } catch {
            setDisableMenu(true);
            setMessage({type: "error", text: t("common.messages.loadError")});
        }
    };

    useEffect(() => {
        const tourParam = searchParams.get(QUERY_PARAMS.TOUR);
        const storedAccount = localStorage.getItem("account");

        if (!tourParam) return;

        if (!storedAccount) {
            navigate(ROUTES.HOME, {replace: true});
            return;
        }

        setShowSearchDialog(true);
    }, [searchParams, account, navigate]);

    useEffect(() => {
        if (account) loadAll();
    }, [account]);

    useDbUpdates(!!account, loadAll);

    useEffect(() => {
        localStorage.setItem("hideHistorical", hideHistorical.toString());
    }, [hideHistorical]);

    const handleEditMembership = (membership: TourMembership) => {
        setEditMembership(membership);
        setEditTour(membership.tour);
        setMembershipDialogMode("edit");
    };

    const handleEditTour = (membership: TourMembership) => {
        setEditMembership(membership);
        setEditTour(membership.tour);
        setTourDialogMode("edit");
    };

    const handleCreateTour = () => {
        setEditTour(null);
        setTourDialogMode("create");
    };

    const closeDialog = () => {
        setEditTour(null);
        setEditMembership(null);
        setTourDialogMode(null);
        setMembershipDialogMode(null);
    };

    function hideTour(tour: Tour): boolean {
        return hideHistorical && isDateInPast(tour.validTo);
    }

    return (
        <div>
            {title}

            <div className="contact-section">
                {!disableMenu &&
                    <div className="menu-row">
                        <Menu
                            items={[
                                {label: t("tours.buttons.addTourMembership"), action: () => setShowSearchDialog(true)},
                                {label: t("tours.buttons.addTour"), action: handleCreateTour},
                                {label: hideHistoricalLabel, action: () => setHideHistorical(!hideHistorical)},
                            ]}
                        />
                    </div>
                }

                <MessageBox message={message}/>

                <div className="contact-grid">
                    {tourMemberships
                        .filter(t => !hideTour(t.tour))
                        .slice()
                        .sort((a, b) => {
                            const fromDiff = a.tour.validFrom.localeCompare(b.tour.validFrom);
                            if (fromDiff !== 0) return fromDiff;

                            return a.tour.validTo.localeCompare(b.tour.validTo);
                        })
                        .map(membership => (
                            <TourCard
                                key={membership.tour.id}
                                membership={membership}
                                onEditMembership={() => handleEditMembership(membership)}
                                onEditTour={() => handleEditTour(membership)}
                                onReloadMemberships={loadAll}
                                onSaved={loadAll}
                            />
                        ))}
                </div>
            </div>

            {tourDialogMode === "create" && (
                <TourDialog
                    tour={null}
                    membership={null}
                    showOtherMembers={false}
                    onReloadMembershipsInTour={loadAll}
                    onClose={closeDialog}
                    onSaved={loadAll}
                    renderForm={({setMessage, onSaved}) => (
                        <TourCreateForm
                            shareEmergencyContacts={contacts.filter(c => c.shareData).map(c => c.id)}
                            setMessage={setMessage}
                            onSaved={() => {
                                loadAll();
                                closeDialog();
                            }}
                        />
                    )}
                />
            )}

            {membershipDialogMode === "edit" && editMembership && editTour && (
                <TourMembershipDialog
                    membership={editMembership}
                    tour={editTour}
                    accountEmergencyContacts={contacts}
                    onClose={closeDialog}
                    onSaved={loadAll}
                />
            )}

            {showSearchDialog && (
                <TourSearchDialog
                    accountEmergencyContacts={contacts}
                    tourMemberships={tourMemberships}
                    onReloadTours={loadAll}
                    onClose={() => setShowSearchDialog(false)}
                />
            )}
        </div>
    );
}
